import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const SERVICES = ["LED Video Walls", "Professional Audio", "Stage Lighting", "Stage & Truss", "Live Production", "Full Package"];
const EVENT_TYPES = ["School Annual Day", "College Fest", "Concert", "Political Rally", "Convocation", "Temple / Religious", "Wedding", "Corporate", "Other"];

const field = "w-full bg-[#050505] border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:border-[#F5B841] focus:outline-none focus:ring-1 focus:ring-[#F5B841] transition-colors";

export default function QuoteForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", event_date: "", event_type: "", message: "" });
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (s) => setServices((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.event_type) {
      toast.error("Please fill name, phone, email and event type.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", { ...form, services });
      setDone(true);
      toast.success("Request received! Our team will reach out shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="bg-[#0a0a0a] border-t border-white/10 py-24 md:py-32" data-testid="quote-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-4">
          <span className="text-xs tracking-[0.35em] text-[#F5B841]">GET IN TOUCH</span>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-4 leading-[0.95]">
            Request a <span className="text-[#F5B841]">Quote</span>
          </h2>
          <p className="text-white/55 mt-6 font-light max-w-sm">
            Tell us about your event and we&apos;ll craft a tailored production plan. No pricing surprises — just a clear,
            custom quote.
          </p>
          <div className="mt-10 space-y-3 text-white/70">
            <a href="tel:9866633016" className="block hover:text-[#F5B841] transition-colors">+91 98666 33016</a>
            <a href="tel:9966633016" className="block hover:text-[#F5B841] transition-colors">+91 99666 33016</a>
            <a href="mailto:bhaaskarproductions@gmail.com" className="block hover:text-[#F5B841] transition-colors">bhaaskarproductions@gmail.com</a>
            <p className="text-white/40 text-sm pt-2">Hyderabad, Telangana, India</p>
          </div>
        </div>

        <div className="lg:col-span-8">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="quote-success"
              className="border border-[#F5B841]/40 bg-[#050505] p-12 h-full flex flex-col items-start justify-center"
            >
              <div className="h-14 w-14 rounded-full bg-[#F5B841] flex items-center justify-center">
                <Check className="w-7 h-7 text-black" />
              </div>
              <h3 className="font-display text-3xl mt-6">Request received.</h3>
              <p className="text-white/55 mt-3 max-w-md">
                Thank you, {form.name || "there"}. Our production team will contact you shortly to plan your event.
              </p>
              <button
                onClick={() => { setDone(false); setForm({ name: "", phone: "", email: "", event_date: "", event_type: "", message: "" }); setServices([]); }}
                className="mt-8 text-[#F5B841] border border-[#F5B841]/40 px-6 py-3 hover:bg-[#F5B841] hover:text-black transition-colors"
                data-testid="quote-another-btn"
              >
                Submit another request
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="quote-form">
              <input data-testid="quote-name" className={field} placeholder="Full name *" value={form.name} onChange={set("name")} />
              <input data-testid="quote-phone" className={field} placeholder="Phone number *" value={form.phone} onChange={set("phone")} />
              <input data-testid="quote-email" type="email" className={field} placeholder="Email address *" value={form.email} onChange={set("email")} />
              <div>
                <label className="text-white/45 text-xs tracking-widest">EVENT DATE</label>
                <input data-testid="quote-date" type="date" className={`${field} [color-scheme:dark] mt-3`} value={form.event_date} onChange={set("event_date")} />
              </div>

              <div className="md:col-span-2">
                <label className="text-white/45 text-xs tracking-widest">EVENT TYPE *</label>
                <div className="flex flex-wrap gap-2 mt-3">
                  {EVENT_TYPES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      data-testid={`event-type-${t}`}
                      onClick={() => setForm({ ...form, event_type: t })}
                      className={`px-4 py-2 text-sm border transition-colors ${
                        form.event_type === t ? "bg-[#F5B841] text-black border-[#F5B841]" : "border-white/15 text-white/60 hover:border-white/40"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="text-white/45 text-xs tracking-widest">SERVICES NEEDED</label>
                <div className="flex flex-wrap gap-2 mt-3">
                  {SERVICES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`service-${s}`}
                      onClick={() => toggle(s)}
                      className={`px-4 py-2 text-sm border transition-colors ${
                        services.includes(s) ? "bg-white text-black border-white" : "border-white/15 text-white/60 hover:border-white/40"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <textarea data-testid="quote-message" rows={4} className={`${field} md:col-span-2 resize-none`} placeholder="Tell us about your event…" value={form.message} onChange={set("message")} />

              <button
                type="submit"
                disabled={loading}
                data-testid="quote-submit-btn"
                className="md:col-span-2 bg-[#F5B841] text-black font-semibold py-4 hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</> : "Send Request"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
